import { useEffect, useRef, useState } from "react";
import { X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api from "../lib/api";

const STATES = [
  "Andhra Pradesh","Telangana","Karnataka","Tamil Nadu","Kerala","Maharashtra",
  "Goa","Gujarat","Rajasthan","Madhya Pradesh","Odisha","West Bengal","Bihar",
  "Jharkhand","Chhattisgarh","Uttar Pradesh","Uttarakhand","Himachal Pradesh",
  "Punjab","Haryana","Delhi","Assam","Sikkim","Manipur","Meghalaya","Mizoram",
  "Nagaland","Tripura","Arunachal Pradesh","Jammu and Kashmir","Ladakh",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Lakshadweep","Puducherry",
];

/**
 * UNIFIED ADDRESS MODEL
 * One schema, used by:
 *   - Account → Saved Addresses (add/edit)
 *   - Checkout → Saved Addresses (add/edit), order placement
 *   - Backend `UnifiedAddress` + `SavedAddress*` + Order.address
 */
export const EMPTY_ADDRESS = {
  full_name: "",
  phone: "",
  house_no: "",
  street: "",
  landmark: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
  is_default: false,
};

/**
 * Returns null if the address is valid, otherwise a human-readable error string.
 * Used both by Account and Checkout so validation never drifts.
 */
export function validateAddress(a) {
  if (!a) return "Address is missing";
  const required = ["full_name", "phone", "house_no", "street", "city", "state", "pincode"];
  for (const k of required) {
    if (!String(a?.[k] ?? "").trim()) return "Please fill all required fields";
  }
  if (!/^[6-9]\d{9}$/.test(String(a.phone).replace(/\D/g, ""))) {
    return "Enter a valid 10-digit Indian phone number";
  }
  if (!/^\d{6}$/.test(String(a.pincode))) {
    return "Pincode must be 6 digits";
  }
  return null;
}

/**
 * Shared address create/edit modal. Used by Account page and Checkout.
 * Caller owns the data state and the save handler.
 *
 * iter77: as soon as the customer types a 6-digit pincode, we call
 * `GET /api/shipping/pincode/:pincode` to (a) validate it is actually
 * serviceable by Shiprocket and (b) auto-populate + lock the city and
 * state fields.  If Shiprocket rejects the pincode the exact error is
 * surfaced inline and save is blocked — no manual override of a
 * non-serviceable pincode is permitted.
 */
export default function AddressModal({ mode = "add", data, onChange, onClose, onSave, saving = false }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("address-modal-open");
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove("address-modal-open");
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const f = (k) => (e) => onChange({ ...data, [k]: e.target.value });

  // ── Shiprocket pincode validation + auto-fill ─────────────────────────
  // Track the last pincode we validated so we don't refetch on re-renders,
  // and so we can wipe stale city/state when the pincode is edited.
  const [pinStatus, setPinStatus] = useState("idle"); // idle | checking | ok | error
  const [pinError, setPinError] = useState("");
  const lastCheckedRef = useRef(null);
  const abortRef = useRef(null);

  const pin = String(data?.pincode || "").trim();

  useEffect(() => {
    // Only validate 6-digit numeric pincodes.
    if (!/^\d{6}$/.test(pin)) {
      // If they've partially typed / cleared the pincode, clear the lock.
      if (lastCheckedRef.current && lastCheckedRef.current !== pin) {
        lastCheckedRef.current = null;
        setPinStatus("idle");
        setPinError("");
      }
      return;
    }
    if (lastCheckedRef.current === pin) return;
    lastCheckedRef.current = pin;
    setPinStatus("checking");
    setPinError("");
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    api.get(`/shipping/pincode/${pin}`, { signal: ctrl.signal })
      .then((r) => {
        if (ctrl.signal.aborted) return;
        const city = r.data?.city || "";
        const state = r.data?.state || "";
        setPinStatus("ok");
        setPinError("");
        // iter88: only auto-fill city/state if they are still empty.
        // Never OVERWRITE what the customer already typed — earlier
        // versions were destructive.
        onChange({
          ...data,
          pincode: pin,
          city: data?.city || city || "",
          state: data?.state || state || "",
        });
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        const msg =
          err?.response?.data?.detail ||
          "Couldn't verify this pincode with our shipping partner. You can still save the address — we'll re-check during checkout.";
        setPinStatus("error");
        setPinError(msg);
        // iter88: DO NOT wipe user-typed city/state on error — earlier
        // code did `onChange({ ...data, city: "", state: "" })` which
        // deleted valid input every time Shiprocket had a hiccup.
      });
    return () => ctrl.abort();
    // Intentionally exclude `data` and `onChange` from deps — they change
    // every keystroke and would cancel this effect on every letter.
  }, [pin]);

  // iter88: pincode serviceability is INFORMATIONAL only. Save is blocked
  // solely by field-level validation (the same `validateAddress()` the
  // caller re-runs on click) + the in-flight save flag. This ensures a
  // Shiprocket outage / rate-limit / transient timeout can NEVER prevent
  // a customer from saving their address and placing an order.
  const localValidationError = validateAddress(data);
  const saveBlocked = saving || !!localValidationError;
  // City/state are only auto-filled — never LOCKED — so if Shiprocket is
  // offline the customer can always type them manually.
  const cityStateLocked = false;

  return (
    <div
      className="fixed inset-0 z-[150] bg-brand-dark/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6 animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="address-modal"
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-brand-line sticky top-0 bg-white z-10">
          <h3 className="font-serif text-2xl text-brand-dark">
            {mode === "add" ? "Add new address" : "Edit address"}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close"
            className="p-2 rounded-full text-brand-dark active:bg-brand-subtle"
            data-testid="address-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name" testid="addr-full-name">
            <input className="input-field" value={data.full_name || ""} onChange={f("full_name")} required data-testid="addr-full-name-input" />
          </Field>
          <Field label="Phone (10-digit mobile)" testid="addr-phone">
            <input className="input-field" value={data.phone || ""} onChange={f("phone")} inputMode="tel" maxLength={10} required data-testid="addr-phone-input" />
          </Field>
          <Field label="House / Flat number" testid="addr-house-no">
            <input className="input-field" value={data.house_no || ""} onChange={f("house_no")} required data-testid="addr-house-no-input" />
          </Field>
          <Field label="Street / Area" testid="addr-street">
            <input className="input-field" value={data.street || ""} onChange={f("street")} required data-testid="addr-street-input" />
          </Field>
          <Field label="Landmark (optional)" testid="addr-landmark">
            <input className="input-field" value={data.landmark || ""} onChange={f("landmark")} data-testid="addr-landmark-input" />
          </Field>
          <Field label="PIN Code" testid="addr-pincode">
            <div className="relative">
              <input
                className="input-field pr-9"
                value={data.pincode || ""}
                onChange={f("pincode")}
                inputMode="numeric"
                maxLength={6}
                required
                data-testid="addr-pincode-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                {pinStatus === "checking" && (
                  <Loader2 className="w-4 h-4 text-brand-muted animate-spin" data-testid="addr-pincode-loading" />
                )}
                {pinStatus === "ok" && (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" data-testid="addr-pincode-ok" />
                )}
                {pinStatus === "error" && (
                  <AlertCircle className="w-4 h-4 text-brand-chili" data-testid="addr-pincode-bad" />
                )}
              </span>
            </div>
            {pinStatus === "error" && (
              <p className="text-xs text-brand-chili mt-1.5" data-testid="addr-pincode-error">{pinError}</p>
            )}
            {pinStatus === "ok" && (
              <p className="text-xs text-emerald-700 mt-1.5" data-testid="addr-pincode-serviceable">
                Serviceable — city &amp; state suggested.
              </p>
            )}
          </Field>
          <Field label="City" testid="addr-city">
            <input
              className={`input-field ${cityStateLocked ? "bg-brand-subtle text-brand-muted cursor-not-allowed" : ""}`}
              value={data.city || ""}
              onChange={f("city")}
              required
              readOnly={cityStateLocked}
              data-testid="addr-city-input"
            />
          </Field>
          <Field label="State" testid="addr-state">
            <select
              className={`input-field ${cityStateLocked ? "bg-brand-subtle text-brand-muted cursor-not-allowed" : ""}`}
              value={data.state || ""}
              onChange={f("state")}
              required
              disabled={cityStateLocked}
              data-testid="addr-state-input"
            >
              <option value="">Select state</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              {/* If Shiprocket returns a state not in our list (e.g.
                  spelling variant), still show it selected. */}
              {cityStateLocked && data.state && !STATES.includes(data.state) && (
                <option value={data.state}>{data.state}</option>
              )}
            </select>
          </Field>
          <Field label="Country" testid="addr-country">
            <input className="input-field bg-brand-subtle text-brand-muted" value={data.country || "India"} readOnly data-testid="addr-country-input" />
          </Field>
          <label className="sm:col-span-2 flex items-center gap-2 mt-1 text-sm text-brand-dark cursor-pointer">
            <input
              type="checkbox"
              checked={!!data.is_default}
              onChange={(e) => onChange({ ...data, is_default: e.target.checked })}
              className="w-4 h-4 accent-brand-chili"
              data-testid="addr-default-checkbox"
            />
            Set as default address
          </label>
        </div>

        <div className="px-6 py-4 border-t border-brand-line sticky bottom-0 bg-white">
          {localValidationError && (
            <p className="text-xs text-brand-chili mb-2 text-right" data-testid="address-modal-validation-hint">
              {localValidationError}
            </p>
          )}
          <div className="flex items-center justify-end gap-3">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="address-modal-cancel">
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saveBlocked}
              title={saveBlocked ? (localValidationError || "Saving…") : ""}
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="address-modal-save"
            >
              {saving ? "Saving…" : (mode === "add" ? "Save address" : "Save changes")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="label-field">{label}</label>
      {children}
    </div>
  );
}
